import type { NavigationItemId } from '@config/navigation/navigationItems';
import type { SettingsSection } from '@config/paths/settingsSections';
import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { PRODUCT_MODULES, type ProductModuleConfig, type ProductModuleId } from './productModules';

/**
 * Lookup helpers — derived from the registry so there's a single source of truth.
 */

/** Map from NavigationItemId → ProductModuleId for items that are gated */
export const NAV_ITEM_TO_MODULE: Partial<Record<NavigationItemId, ProductModuleId>> =
	Object.fromEntries(
		Object.entries(PRODUCT_MODULES).flatMap(([moduleId, config]) =>
			config.navItems.map((navId) => [navId, moduleId as ProductModuleId]),
		),
	);

/** Map from SettingsSection → ProductModuleId for settings pages that are gated */
export const SETTINGS_SECTION_TO_MODULE: Partial<Record<SettingsSection, ProductModuleId>> =
	Object.fromEntries(
		Object.entries(PRODUCT_MODULES).flatMap(([moduleId, config]) =>
			config.settingsSections.map((section) => [section, moduleId as ProductModuleId]),
		),
	);

/**
 * Map from onboarding product catalog ID → first settings section.
 * Used to redirect users to the right settings page after completing onboarding.
 */
export const ONBOARDING_PRODUCT_TO_SETTINGS: Record<string, SettingsSection> = Object.fromEntries(
	(Object.values(PRODUCT_MODULES) as ProductModuleConfig[])
		.filter((config) => config.onboardingProductId && config.settingsSections.length > 0)
		.map((config) => [config.onboardingProductId, config.settingsSections[0]]),
);

/**
 * Pure gating functions — single source of truth for subscription checks.
 */

/** Check if a specific product module is locked for a given provider. */
export function isModuleLocked(
	provider: ServicesProvider | null | undefined,
	moduleId: ProductModuleId,
): boolean {
	if (!provider) return false;
	const module = PRODUCT_MODULES[moduleId];
	const serviceConfig = provider.config?.[module.service];
	return serviceConfig?.supported !== true;
}

/** Check if a settings section is locked, resolving through the section→module map. */
export function getSettingsSectionGate(
	provider: ServicesProvider | null | undefined,
	section: SettingsSection,
): { locked: boolean; moduleId?: ProductModuleId } {
	const moduleId = SETTINGS_SECTION_TO_MODULE[section];
	if (!moduleId) return { locked: false };
	return { locked: isModuleLocked(provider, moduleId), moduleId };
}

/** Returns the set of NavigationItemIds that are currently locked behind a subscription. */
export function getLockedNavItems(
	provider: ServicesProvider | null | undefined,
): Set<NavigationItemId> {
	const locked = new Set<NavigationItemId>();
	if (!provider) return locked;
	for (const [navId, moduleId] of Object.entries(NAV_ITEM_TO_MODULE)) {
		if (!moduleId) continue;
		if (isModuleLocked(provider, moduleId)) {
			locked.add(navId as NavigationItemId);
		}
	}
	return locked;
}
